import { isDestinationType } from '@/features/destination/destination-form-fields/destination-types'
import type { DestinationValidationError } from '@/features/destination/destination-form-model/types'
import type {
  DestinationCenterPoint,
  DestinationFormValue,
  DestinationTranslationValue,
  IAreaBoundary,
} from '@/shared/api/types/destination-types'
import { hasRequiredTranslations } from '@/shared/utils/language-utils'

/** Country, province, slug and destination type identify the destination; images are mandatory; uploads must have finished. */
export function validateDestinationDetails(
  form: DestinationFormValue,
  isUploading: boolean,
): DestinationValidationError | null {
  if (isUploading) {
    return {
      key: 'tour.waitMediaUpload',
      defaultValue: 'Please wait for image upload to finish.',
    }
  }
  if (
    !form.countryId ||
    !form.provinceId ||
    !form.slug.trim() ||
    !isDestinationType(form.destinationType)
  ) {
    return { key: 'common.requiredFieldsError', defaultValue: 'Please fill required fields' }
  }
  if (!form.banner.trim() || !form.thumbnail.trim()) {
    return { key: 'tour.imagesRequired', defaultValue: 'Please upload banner and thumbnail.' }
  }
  return null
}

/** Both the drawn boundary and the centre point are required before the map can be saved. */
export function validateDestinationMap(
  boundary: IAreaBoundary | null,
  center: DestinationCenterPoint | null,
): DestinationValidationError | null {
  return boundary && center
    ? null
    : {
        key: 'destination.requiredAreaBoundary',
        defaultValue: 'Please draw the area boundary and set the center point.',
      }
}

/** Every language needs a name and a description. */
export function validateDestinationTranslations(
  translations: DestinationTranslationValue[],
): DestinationValidationError | null {
  return hasRequiredTranslations(translations, ['name', 'description'])
    ? null
    : { key: 'destination.allLanguagesTranslationRequired' }
}

export interface DestinationSubmissionInput {
  form: DestinationFormValue
  isUploading: boolean
  /** Translations already trimmed by `normalizeDestinationTranslations`. */
  translations: DestinationTranslationValue[]
  /** Skip the translation check when the user did not change them (only possible when editing). */
  validateTranslations: boolean
}

/** Validates the whole create form in the order the blocks are filled in; returns the first failure. */
export function validateDestinationSubmission(
  input: DestinationSubmissionInput,
): DestinationValidationError | null {
  return (
    validateDestinationDetails(input.form, input.isUploading) ??
    validateDestinationMap(input.form.areaBoundary, input.form.centerPoint) ??
    (input.validateTranslations ? validateDestinationTranslations(input.translations) : null)
  )
}
